import { ChangeEvent, ElementType, FC, FocusEventHandler, KeyboardEventHandler } from 'react';
import {
    BaseTextFieldProps,
    Box,
    BoxProps,
    FormControl,
    InputBaseComponentProps,
    InputLabelProps,
    StandardTextFieldProps,
    TextField as MuiTextField,
    SxProps,
    Theme,
} from '@mui/material';
import { ReadOnly } from '../ReadOnly';
import { useFieldsDisabled, useFieldsReadonly } from '../Form';
import { hideTooltipIconWhenLabelNotFloating, ITooltipIconProps, renderTooltipLabel } from '../Tooltip';
import { containsForbiddenChars, removeForbiddenChars, DEFAULT_FORBIDDEN_CHARS } from '../../utils/inputValidation';

export type TextFieldSize = 'small' | 'medium' | undefined;

export type TextFieldColor = 'primary' | 'secondary' | undefined;

export interface ITextFieldProps {
    id?: string;
    label?: string;
    name?: string;
    multiline?: boolean;
    rows?: string | number;
    value?: string;
    color?: TextFieldColor;
    disabled?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    readonly?: boolean;
    error?: boolean;
    type?: string;
    size?: TextFieldSize;
    onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
    onFocus?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
    onKeyDown?: KeyboardEventHandler<HTMLDivElement> | undefined;
    onKeyUp?: KeyboardEventHandler<HTMLDivElement> | undefined;
    testId?: string;
    inputRef?: BaseTextFieldProps['inputRef'];
    sx?: BaseTextFieldProps['sx'];
    required?: boolean;
    helperText?: BaseTextFieldProps['helperText'];
    onDoubleClick?: BaseTextFieldProps['onDoubleClick'];
    tooltip?: ITooltipIconProps;
    autoComplete?: string;
    InputProps?: StandardTextFieldProps['InputProps'];
    placeholder?: string;
    inputLabelProps?: InputLabelProps;
    stacked?: boolean;
    labelBoxSx?: BoxProps['sx'];
    containerSx?: BoxProps['sx'];
    inputProps?: InputBaseComponentProps;
    textFieldLabel?: string;
    numericOnly?: boolean;
    readonlySx?: SxProps<Theme>;
    dynamicLabelSize?: boolean;
    forbiddenChars?: string[];
}

const getSizeSX = (size: TextFieldSize): BaseTextFieldProps['sx'] => {
    if (size === 'small') {
        return {
            '.MuiOutlinedInput-input': {
                padding: '8px 14.5px !important',
                height: '1.4375em !important',
            },
        };
    }
    return {};
};

const getColorSX = (color: TextFieldColor): BaseTextFieldProps['sx'] => {
    if (color === 'secondary') {
        return {
            '.MuiInputLabel-root.Mui-focused, .MuiInputLabel-root.MuiFormLabel-filled': {
                color: 'white',
            },
            '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'white',
            },
        };
    }
    return {};
};

const getSX = (size: TextFieldSize, color: TextFieldColor, sx: BaseTextFieldProps['sx']): BaseTextFieldProps['sx'] => {
    return [
        getSizeSX(size),
        getColorSX(color),
        hideTooltipIconWhenLabelNotFloating,
        { '>.MuiFormLabel-root:not(.MuiFormLabel-filled):not(.Mui-focused)': { top: -9 } },
        ...(Array.isArray(sx) ? sx : [sx]),
    ];
};

/** Stable id for side labels when callers omit `id` (common on profile forms). */
function labelToFieldId(label: string): string {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

export const TextField: FC<ITextFieldProps> = ({
    id,
    label,
    name,
    multiline,
    rows,
    value,
    color,
    disabled,
    onChange,
    readonly,
    error,
    type,
    size,
    onBlur,
    onFocus,
    onKeyDown,
    onKeyUp,
    testId,
    inputRef,
    sx,
    required,
    helperText,
    onDoubleClick,
    tooltip,
    autoComplete,
    InputProps,
    placeholder,
    inputLabelProps,
    stacked,
    labelBoxSx,
    inputProps,
    textFieldLabel,
    containerSx,
    numericOnly = false,
    readonlySx,
    dynamicLabelSize = false,
    forbiddenChars,
}) => {
    const resolvedId =
        id ??
        name ??
        (testId != null ? String(testId) : undefined) ??
        (typeof label === 'string' ? labelToFieldId(label) : undefined);

    const isReadonly = useFieldsReadonly(readonly);
    const isDisabled = useFieldsDisabled(disabled);
    if (isReadonly) {
        return (
            <ReadOnly
                testId={testId}
                label={label}
                value={value}
                sx={readonlySx}
                stackColumns={stacked}
                tooltip={tooltip}
            />
        );
    }

    // Render label with intelligent handling for long continuous strings
    const renderLabel = () => {
        if (!label) {
            return null;
        }

        // Detect long continuous strings (no spaces and longer than typical width)
        const isLongContinuousString = typeof label === 'string' && !label.includes(' ') && label.length > 15;

        // Handle dynamicLabelSize formatting
        const formatLabel = (text: string) => {
            if (dynamicLabelSize && text.includes(' ')) {
                return text.replace(' ', '\n');
            }
            return text;
        };

        const finalLabel = dynamicLabelSize && typeof label === 'string' ? formatLabel(label) : label;

        return (
            <Box component="label" htmlFor={resolvedId}>
                <Box
                    sx={{
                        width: stacked ? 160 : { xs: '100%', md: 135 },
                        marginRight: stacked ? '' : { xs: 0, md: '25px' },
                        marginBottom: stacked ? 0 : { xs: 0.5, md: 0 },
                        display: 'flex',
                        alignItems: 'center',
                        // For long continuous strings, allow text wrapping within the fixed width
                        wordBreak: isLongContinuousString ? 'break-word' : 'normal',
                        overflowWrap: 'break-word',
                        lineHeight: isLongContinuousString ? 1.2 : 'normal',
                        hyphens: isLongContinuousString ? 'auto' : 'none',
                        ...(dynamicLabelSize && {
                            '& > span': {
                                whiteSpace: 'pre-line',
                            },
                        }),
                        ...labelBoxSx,
                    }}
                >
                    {renderTooltipLabel({ tooltip, label: finalLabel })}
                    {required && (
                        <Box
                            component="span"
                            sx={{
                                marginLeft: '4px', // Add spacing between label and asterisk
                            }}
                        >
                            *
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (onChange === undefined) {
            return;
        }

        let inputValue = event.target.value;

        // Check for forbidden characters validation and create a new event with the cleaned value
        const charsToFilter = forbiddenChars || DEFAULT_FORBIDDEN_CHARS;
        if (charsToFilter && charsToFilter.length > 0) {
            if (containsForbiddenChars(inputValue, charsToFilter)) {
                inputValue = removeForbiddenChars(inputValue, charsToFilter, true, name);

                const cleanedEvent = {
                    ...event,
                    target: {
                        ...event.target,
                        value: inputValue,
                    },
                };
                // Call onChange with cleaned value
                if (numericOnly) {
                    if (inputValue === '' || !isNaN(Number(inputValue))) {
                        onChange(cleanedEvent);
                    }
                } else {
                    onChange(cleanedEvent);
                }
                return;
            }
        }

        if (numericOnly) {
            if (inputValue === '' || !isNaN(Number(inputValue))) {
                onChange(event);
            }
        } else {
            onChange(event);
        }
    };

    // MUI multiline defaults to TextareaAutosize, which renders a hidden mirror <textarea>
    // without a label (WAVE: missing form label). Use a native textarea instead.
    const nativeInputIdProps = resolvedId ? { id: resolvedId } : {};
    const multilineA11yProps = multiline && typeof label === 'string' ? { 'aria-label': label } : {};
    const baseInputProps = {
        ...nativeInputIdProps,
        ...multilineA11yProps,
        'data-testid': testId,
        style: multiline ? { overflow: 'auto', height: '100px' } : {},
        ...inputProps,
    };
    const mergedInputProps =
        multiline || InputProps != null
            ? {
                  ...InputProps,
                  ...(multiline && InputProps?.inputComponent == null
                      ? { inputComponent: 'textarea' as ElementType<InputBaseComponentProps> }
                      : {}),
                  inputProps: {
                      ...nativeInputIdProps,
                      ...multilineA11yProps,
                      ...InputProps?.inputProps,
                      ...inputProps,
                  },
              }
            : InputProps;

    return (
        <Box
            sx={{
                ...(stacked
                    ? { display: 'flex', flexDirection: 'column', width: '100%' }
                    : {
                          display: 'flex',
                          alignItems: { xs: 'stretch', md: 'center' },
                          flexDirection: { xs: 'column', md: 'row' },
                          width: '100%',
                      }),
                ...containerSx,
            }}
        >
            {renderLabel()}
            <FormControl fullWidth={true}>
                <MuiTextField
                    id={multiline ? undefined : resolvedId}
                    name={name}
                    variant="outlined"
                    multiline={multiline}
                    rows={rows}
                    value={value}
                    disabled={isDisabled}
                    onChange={onChangeHandler}
                    error={error}
                    type={type}
                    fullWidth={true}
                    size={size}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    sx={getSX(size, color, sx)}
                    inputProps={baseInputProps}
                    inputRef={inputRef}
                    required={required}
                    helperText={helperText}
                    onDoubleClick={onDoubleClick}
                    InputLabelProps={{
                        sx: {
                            display: 'flex',
                        },
                        ...inputLabelProps,
                    }}
                    autoComplete={autoComplete}
                    InputProps={mergedInputProps}
                    placeholder={placeholder}
                    label={textFieldLabel}
                />
            </FormControl>
        </Box>
    );
};
